import _ from 'underscore';
import React, { useState, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';

import { I18nContext } from '@bingads-webui-react/i18n-context';

import { LanguageCountrySelection } from './language-and-country-selection';
import { VIEWS } from '../consts';
import { Tile } from '../tile';
import type { LanguageCountrySelectionTileProps } from '../types';

export function LanguageCountrySelectionTile(props: LanguageCountrySelectionTileProps) {
  const {
    selectedLanguage,
    onLanguageSelectionChange,
    campaign,
    useConfirmDialog,
    languages,
    countryOptions,
    selectedCountry,
    onCountrySelected,
    initCountryCode,
    isCountrySelectDisabled,
    shouldShowCountryChangedWarning,
    uiActivity,
    shouldShowLanguageChangedWarning,
    setIsCampaignDeleteConfirmed,
    subtitle,
    onNext,
    onBack,
    confirmDialogClassName,
  } = props;

  const [error, setError] = useState<string>();

  const i18n = useContext(I18nContext);
  const campaignLanguage = _.get(campaign, 'Language');

  const onLanguageBack = useCallback(() => {
    onBack(VIEWS.GOALS);
    setError(null);
  }, [onBack]);

  const onConfirmNext = useCallback(() => {
    if (initCountryCode !== selectedCountry) {
      uiActivity.trace(`User update country from ${initCountryCode} to ${selectedCountry}`);
    }
    setIsCampaignDeleteConfirmed(true);
    onNext(VIEWS.WEBSITE);
  }, [initCountryCode, selectedCountry, setIsCampaignDeleteConfirmed, onNext, uiActivity]);

  const onCancelDialog = useCallback(() => {
    const previousLanguage = campaignLanguage || selectedLanguage;
    onLanguageSelectionChange(previousLanguage);
    onCountrySelected(null, {
      key: initCountryCode,
    });
  }, [onLanguageSelectionChange, campaignLanguage, onCountrySelected, initCountryCode, selectedLanguage]);

  const [
    changeDialog,
    showChangeDialog,
    isChangeDialogVisible,
  ] = useConfirmDialog({
    title: i18n.getString(_TL_('Confirm changes')),
    content: i18n.getString(_TL_('When you confirm a change to either your language or business location, you\'ll have to create a new campaign.')),
    confirmText: i18n.getString(_TL_('Confirm')),
    onConfirm: onConfirmNext,
    cancelText: i18n.getString(_TL_('Discard changes')),
    onCancel: onCancelDialog,
    className: confirmDialogClassName,
  }, {
    activity: uiActivity,
    dialogName: 'UpWizardChangeLanguageOrCountryDialog',
  });

  const onLanguageNext = useCallback(() => {
    if (_.isEmpty(selectedLanguage)) {
      setError(i18n.getString(_TL_('Please select a language.')));
    } else if (shouldShowLanguageChangedWarning || shouldShowCountryChangedWarning) {
      showChangeDialog();
    } else {
      onNext(VIEWS.WEBSITE);
    }
  }, [selectedLanguage, shouldShowLanguageChangedWarning, shouldShowCountryChangedWarning, i18n, showChangeDialog, onNext]);

  const onLanguageSelected = useCallback((_event: React.FormEvent<HTMLDivElement>, selection?: { key: string | number }) => {
    if (!selection) {
      return;
    }
    setError(null);
    onLanguageSelectionChange(selection.key as string);
    if (uiActivity) {
      uiActivity.trace(`LanguageSelection: change to ${selection.key}`);
    }
  }, [onLanguageSelectionChange, uiActivity]);

  return (
    <Tile
      title={i18n.getString(_TL_('Tell us about your business'))}
      subtitle={subtitle}
      onNext={onLanguageNext}
      onBack={onLanguageBack}
    >
      <LanguageCountrySelection
        selectedLanguage={selectedLanguage}
        onLanguageSelected={onLanguageSelected}
        languages={languages}
        countryOptions={countryOptions}
        selectedCountry={selectedCountry}
        onCountrySelected={onCountrySelected}
        isCountrySelectDisabled={isCountrySelectDisabled}
        error={error}
      />
      {isChangeDialogVisible && changeDialog}
    </Tile>
  );
}

LanguageCountrySelectionTile.propTypes = {
  selectedLanguage: PropTypes.string,
  onLanguageSelectionChange: PropTypes.func.isRequired,
  useConfirmDialog: PropTypes.func.isRequired,
  campaign: PropTypes.shape({}),
  uiActivity: PropTypes.shape({
    trace: PropTypes.func,
  }).isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, text: PropTypes.string })),
  countryOptions: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, text: PropTypes.string })),
  selectedCountry: PropTypes.string,
  onCountrySelected: PropTypes.func,
  initCountryCode: PropTypes.string,
  isCountrySelectDisabled: PropTypes.bool,
  shouldShowCountryChangedWarning: PropTypes.bool.isRequired,
  shouldShowLanguageChangedWarning: PropTypes.bool.isRequired,
  setIsCampaignDeleteConfirmed: PropTypes.func.isRequired,
  subtitle: PropTypes.node,
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  confirmDialogClassName: PropTypes.string,
};

LanguageCountrySelectionTile.defaultProps = {
  campaign: null,
  selectedLanguage: null,
  languages: [],
  countryOptions: null,
  selectedCountry: null,
  onCountrySelected: _.noop,
  initCountryCode: null,
  isCountrySelectDisabled: false,
  subtitle: null,
  confirmDialogClassName: 'up-wizard-change-language-or-country-dialog',
};
